import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import {
  useImportHistory,
  useImportBatchDetails,
} from "../../api/queries/adminQueries";
import { useDataImports } from "../../api/queries/adminToolsQueries";
import { dataExchangeApi, saveFile } from "../../api/adminToolsApi";
import ImportTabs from "../admin/data-tools/ImportTabs";
import { csvCell } from "../../utils/csv";
import { formatDateTime } from "../../utils/dateTime";
import {
  importModuleLabel,
  isSyncBatch,
  movedText,
} from "../settings/data-sync/syncFormat";
import "./ImportHistoryPage.css";

const ImportHistory = () => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const historyQuery = useImportHistory({
    limit: itemsPerPage,
    skip: (currentPage - 1) * itemsPerPage,
  });
  const history = historyQuery.data?.history || [];
  const totalCount = historyQuery.data?.totalCount || 0;
  const historyLoading = historyQuery.isLoading;

  // Imports made with Administration → Import (data tools)
  const [toolPage, setToolPage] = useState(1);
  const toolQuery = useDataImports({ page: toolPage, limit: itemsPerPage });
  const toolImports = toolQuery.data?.data || [];
  const toolPages = Math.ceil((toolQuery.data?.total || 0) / itemsPerPage);
  const downloadToolErrors = async (logId) => {
    try {
      saveFile(await dataExchangeApi.importErrors(logId, "xlsx"));
    } catch {
      toast.info("No failed rows for this import.");
    }
  };

  const detailsQuery = useImportBatchDetails(
    showDetailModal ? selectedBatchId : null,
  );
  const selectedBatchDetails = detailsQuery.data || [];
  const detailsLoading = detailsQuery.isLoading;
  // Zoho sync rows are files, not workbook rows
  const syncDetails = isSyncBatch(
    selectedModule,
    Array.isArray(selectedBatchDetails) ? selectedBatchDetails : [],
  );

  useEffect(() => {
    if (detailsQuery.isError) {
      toast.error("Failed to load batch details");
      setShowDetailModal(false);
    }
  }, [detailsQuery.isError]);

  const handleViewDetails = (batchId, moduleName) => {
    setSelectedBatchId(batchId);
    setSelectedModule(moduleName || null);
    setShowDetailModal(true);
  };

  const closeDetails = () => {
    setShowDetailModal(false);
    setSelectedBatchId(null);
  };

  const downloadErrorCSV = () => {
    if (
      !Array.isArray(selectedBatchDetails) ||
      selectedBatchDetails.length === 0
    )
      return;

    // Filter by 'FAILED' AND 'PARTIAL SUCCESS' status
    const problematicRows = selectedBatchDetails.filter(
      (row) => row.status === "FAILED" || row.status === "PARTIAL SUCCESS",
    );

    if (problematicRows.length === 0)
      return toast.info("No errors to download!");

    const headers = syncDetails
      ? ["Row", "File Name", "Employee", "Document", "Status", "Error Message"]
      : ["Row", "Employee", "Status", "Error Message"];
    const rows = problematicRows.map((item) => {
      if (syncDetails) {
        return [
          item.rowNumber,
          csvCell(item.rawData?.fileName || ""),
          csvCell(item.rawData?.employee || ""),
          csvCell(item.rawData?.document || ""),
          item.status,
          csvCell(item.errors || "Unknown Error"),
        ];
      }
      const fName =
        item.rawData?.["First Name"] || item.rawData?.["firstname"] || "";
      const lName =
        item.rawData?.["Last Name"] || item.rawData?.["lastname"] || "";
      const fullName = `${fName} ${lName}`.trim() || "N/A";

      return [
        item.rowNumber,
        `"${fullName}"`,
        item.status,
        `"${item.errors?.replace(/"/g, '""') || "Unknown Error"}"`,
      ];
    });

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `import_report_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil((totalCount || 0) / itemsPerPage);

  // Helper to render badges in modal
  const renderStatusBadge = (status) => {
    let bgClass = "bg-danger";
    let label = status;

    if (status === "SUCCESS" || status === "COMPLETED") bgClass = "bg-success";
    if (status === "PARTIAL SUCCESS") bgClass = "bg-warning text-dark";

    return (
      <span className={`badge badge-fixed-width ${bgClass} fs-060`}>
        {label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4 d-flex justify-content-between align-items-end">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Import</h1>
            <p className="text-muted small mb-0">
              History: every import, with its results and the rows that failed.
            </p>
          </div>
          <div className="text-muted small">
            Total Logs: <strong>{totalCount}</strong>
          </div>
        </div>

        <ImportTabs />
        <h3 className="fw-bold fs-6 mb-2">New employees and Zoho syncs</h3>
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4">Date</th>
                  <th className="ps-4">ID</th>
                  <th className="ps-4">Module</th>
                  <th className="ps-4">File Name</th>
                  <th className="text-center">Results</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                      Fetching history...
                    </td>
                  </tr>
                ) : Array.isArray(history) && history.length > 0 ? (
                  history.map((item) => (
                    <tr key={item.id} className="align-middle">
                      <td className="ps-4 text-nowrap">
                        {formatDateTime(item.createdAt)}
                      </td>
                      <td className="ps-4 text-nowrap">{item.id}</td>
                      <td className="ps-4 text-nowrap">
                        {importModuleLabel(item.moduleName)}
                      </td>
                      <td className="fw-medium text-truncate mw-250px">
                        {item.fileName}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success-subtle text-success me-2">
                          {item.successCount} Success
                        </span>
                        <span className="badge bg-warning-subtle text-warning me-2">
                          {item.partialCount || 0} Partial
                        </span>
                        <span className="badge bg-danger-subtle text-danger">
                          {item.failureCount} Failed
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary px-3 fw-bold"
                          onClick={() =>
                            handleViewDetails(item.id, item.moduleName)
                          }
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      No import logs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card-footer bg-white d-flex justify-content-between py-3 border-top">
            <small className="text-muted">
              Page {currentPage} of {totalPages || 1}
            </small>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-dark px-3"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Prev
              </button>
              <button
                className="btn btn-sm btn-outline-dark px-3"
                disabled={currentPage >= totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid pb-4 px-lg-4">
        <h3 className="fw-bold fs-6 mb-2">Import data (one kind)</h3>
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4">Date</th>
                  <th className="ps-4">Data</th>
                  <th className="ps-4">File Name</th>
                  <th className="text-center">Results</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {toolQuery.isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary"></div>
                    </td>
                  </tr>
                ) : toolImports.length > 0 ? (
                  toolImports.map((item) => (
                    <tr key={item.id} className="align-middle">
                      <td className="ps-4 text-nowrap">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td className="ps-4 text-nowrap">
                        {item.dataset || item.moduleName}
                      </td>
                      <td className="fw-medium text-truncate mw-250px">
                        {item.fileName}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success-subtle text-success me-2">
                          {item.successCount ?? 0} Success
                        </span>
                        <span className="badge bg-danger-subtle text-danger">
                          {item.failureCount ?? 0} Failed
                        </span>
                      </td>
                      <td className="text-end pe-4 text-nowrap">
                        <button
                          className="btn btn-sm btn-outline-primary px-3 fw-bold me-2"
                          onClick={() => handleViewDetails(item.id)}
                        >
                          View Details
                        </button>
                        {(item.failureCount ?? 1) > 0 && (
                          <button
                            className="btn btn-sm btn-outline-danger px-3"
                            onClick={() => downloadToolErrors(item.id)}
                          >
                            <i className="bi bi-download me-1"></i>Errors
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No data tool imports yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {toolPages > 1 && (
            <div className="card-footer bg-white d-flex justify-content-between py-3 border-top">
              <small className="text-muted">
                Page {toolPage} of {toolPages}
              </small>
              <div className="btn-group">
                <button
                  className="btn btn-sm btn-outline-dark px-3"
                  disabled={toolPage === 1}
                  onClick={() => setToolPage((p) => p - 1)}
                >
                  Prev
                </button>
                <button
                  className="btn btn-sm btn-outline-dark px-3"
                  disabled={toolPage >= toolPages}
                  onClick={() => setToolPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailModal && (
        <div className="modal-overlay modal-overlay--dark import-modal-overlay">
          <div className="bg-white rounded-4 shadow-lg overflow-hidden animate__animated animate__zoomIn animate__faster import-dialog">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white">
              <h6 className="mb-0 fw-bold text-uppercase fs-075 text-slate-500">
                Detailed Batch Report
              </h6>
              <button className="btn-close" onClick={closeDetails}></button>
            </div>

            <div className="p-4 overflow-auto">
              {detailsLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : (
                <table className="table table-sm align-middle">
                  <thead className="small text-muted text-uppercase fs-065">
                    <tr>
                      <th className="w-50px">Row</th>
                      {syncDetails && <th>File</th>}
                      <th className="w-200px">Employee</th>
                      {syncDetails && (
                        <>
                          <th>Document</th>
                          <th>Result</th>
                          <th>Moved</th>
                        </>
                      )}
                      <th className="w-100px">Status</th>
                      <th>Reason / Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(selectedBatchDetails) &&
                    selectedBatchDetails.length > 0 ? (
                      selectedBatchDetails.map((item, i) => {
                        const firstName =
                          item.rawData?.["First Name"] ||
                          item.rawData?.["firstname"] ||
                          "";
                        const lastName =
                          item.rawData?.["Last Name"] ||
                          item.rawData?.["lastname"] ||
                          "";
                        const fullName = syncDetails
                          ? item.rawData?.employee || "-"
                          : `${firstName} ${lastName}`.trim() || "N/A";

                        let rowClass = "";
                        if (item.status === "FAILED")
                          rowClass = "table-danger-subtle";
                        if (item.status === "PARTIAL SUCCESS")
                          rowClass = "table-warning-subtle";

                        return (
                          <tr key={item.id || i} className={rowClass}>
                            <td className="fw-bold">{item.rowNumber}</td>
                            {syncDetails && (
                              <td className="text-break">
                                {item.rawData?.fileName ||
                                  (item.rowNumber === 0 ? "Whole sync" : "-")}
                              </td>
                            )}
                            <td className="text-nowrap">{fullName}</td>
                            {syncDetails && (
                              <>
                                <td>{item.rawData?.document || "-"}</td>
                                <td>{item.rawData?.result || "-"}</td>
                                <td>{movedText(item.rawData?.moved)}</td>
                              </>
                            )}
                            <td>{renderStatusBadge(item.status)}</td>
                            <td className="text-muted text-xs lh-14">
                              {item.errors || "-"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={syncDetails ? 8 : 4}
                          className="text-center py-4 text-muted"
                        >
                          No specific row details available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-footer-custom">
              <div>
                {Array.isArray(selectedBatchDetails) &&
                  selectedBatchDetails.some(
                    (r) =>
                      r.status === "FAILED" || r.status === "PARTIAL SUCCESS",
                  ) && (
                    <button
                      className="btn-text-action btn-text-action--danger"
                      onClick={downloadErrorCSV}
                    >
                      <i className="bi bi-download me-1"></i> Download CSV
                      Issues
                    </button>
                  )}
              </div>
              <button
                className="btn btn-secondary btn-sm px-4 fw-bold rounded-3"
                onClick={closeDetails}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ImportHistory;
